import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import type { Property } from '../types';
import { PROPERTY_PAGE_SEGMENTS } from '../config/navigation';

interface PropertyContextType {
  selectedProperty: Property | null;
  setSelectedProperty: (property: Property | null, navigate?: boolean) => void;
  availableProperties: Property[];
  setAvailableProperties: (properties: Property[]) => void;
  isSuperAdmin: boolean;
  propertyIdFromUrl: string | undefined;
}

const PropertyContext = createContext<PropertyContextType | undefined>(undefined);

export function PropertyProvider({ children }: { children: ReactNode }) {
  const { adminUser, refreshSession } = useAuth();
  const { propertyId } = useParams<{ propertyId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [selectedProperty, setSelectedPropertyState] = useState<Property | null>(null);
  const [availableProperties, setAvailableProperties] = useState<Property[]>([]);
  const selectedPropertyRef = useRef<Property | null>(null);

  const isSuperAdmin = adminUser?.isSuperAdmin ?? false;

  // Keep ref in sync with state
  selectedPropertyRef.current = selectedProperty;

  // Restore property selection from URL path on mount and when properties load
  useEffect(() => {
    const pathMatch = location.pathname.match(/\/properties\/([^/]+)(?:\/|$)/);
    const propertyIdFromPath = pathMatch ? pathMatch[1] : null;
    const effectivePropertyId = propertyId || propertyIdFromPath;

    if (effectivePropertyId && availableProperties.length > 0) {
      const property = availableProperties.find((p) => p.id === effectivePropertyId);
      if (property && property.id !== selectedPropertyRef.current?.id) {
        // Different property selected (navigation or first load)
        setSelectedPropertyState(property);
      } else if (!property && selectedPropertyRef.current) {
        setSelectedPropertyState(null);
      }
    }
    // NOTE: Do NOT clear selectedProperty when navigating to non-property routes
    // (e.g. /billing, /organizations). The property stays selected so the sidebar
    // keeps showing the property menu. Explicit deselection happens via
    // setSelectedProperty(null) in the "Back to Admin" button.
  }, [propertyId, location.pathname, availableProperties]);

  // Auto-select first property for non-super, non-org-level admins (if not already on a property route)
  useEffect(() => {
    const alreadyOnPropertyRoute = /\/properties\/[^/]+(?:\/|$)/.test(location.pathname);
    if (!isSuperAdmin && availableProperties.length > 0 && !selectedPropertyRef.current && !alreadyOnPropertyRoute) {
      // Users with org-level roles should land on the org dashboard, not be auto-redirected
      const orgLevelRoles = new Set([
        'org_admin', 'regional_director', 'regional_revenue_manager',
        'regional_marketing_director', 'corporate_ops_director', 'asset_manager',
        'portfolio_owner',
      ]);
      const hasOrgRole = adminUser ? orgLevelRoles.has(adminUser.primaryRole) : false;
      const hasOrgAccess = (adminUser?.accessibleOrganizationIds?.length ?? 0) > 0;

      if (hasOrgRole && hasOrgAccess) {
        // Let them stay on the org dashboard — don't auto-redirect
        return;
      }

      const firstProperty = availableProperties[0];
      setSelectedPropertyState(firstProperty);
      navigate(`/properties/${firstProperty.id}`, { replace: true });
    }
  }, [isSuperAdmin, availableProperties, location.pathname, navigate, adminUser]);

  // Wrapper function to update both state and optionally navigate
  const setSelectedProperty = (property: Property | null, shouldNavigate: boolean = true) => {
    const isPropertySwitch = property && property.id !== selectedPropertyRef.current?.id;
    setSelectedPropertyState(property);

    // Refresh session on property switch to pick up any membership changes
    if (isPropertySwitch) {
      refreshSession();
    }

    // Navigate to property-specific route if requested
    if (shouldNavigate && property) {
      // Extract the page segment from a property route like /properties/:id/<page>
      // Using a regex ensures nested sub-routes (e.g. /settings/advanced) don't break.
      const propertyPages: readonly string[] = PROPERTY_PAGE_SEGMENTS;
      const pageMatch = location.pathname.match(/\/properties\/[^/]+\/([^/]+)/);
      const currentPage = pageMatch?.[1];

      if (currentPage && propertyPages.includes(currentPage)) {
        // Stay on same page type but switch property
        navigate(`/properties/${property.id}/${currentPage}`, { replace: true });
      } else {
        // Default to dashboard
        navigate(`/properties/${property.id}`, { replace: true });
      }
    }
  };

  const value: PropertyContextType = {
    selectedProperty,
    setSelectedProperty,
    availableProperties,
    setAvailableProperties,
    isSuperAdmin,
    propertyIdFromUrl: propertyId,
  };

  return <PropertyContext.Provider value={value}>{children}</PropertyContext.Provider>;
}

export function useProperty() {
  const context = useContext(PropertyContext);
  if (context === undefined) {
    throw new Error('useProperty must be used within a PropertyProvider');
  }
  return context;
}
